
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FormData, TableConfig, DanceFloorConfig, CustomElementConfig, TableShape } from '../types'; // Added CustomElementConfig
import { LOGO_URL_DARK_MODE } from '../constants';

const getShapeLabel = (shape: TableShape): string => {
  switch (shape) {
    case 'round': return 'Redonda';
    case 'rectangular': return 'Rectangular';
    case 'square': return 'Cuadrada';
    case 'living': return 'Living';
    default: return shape;
  }
};

export const generatePdf = async (
  floorPlanElement: HTMLElement | null,
  formData: FormData,
  allTables: TableConfig[],
  danceFloorConfig: DanceFloorConfig,
  customElements: CustomElementConfig[], // New parameter
  fileName: string = 'plano_evento_hba.pdf'
): Promise<void> => {
  if (!floorPlanElement) {
    console.error("Element to capture is null.");
    alert("Error: No se pudo encontrar el contenido del plano para generar el PDF.");
    return;
  }

  const pdfContainer = document.createElement('div');
  pdfContainer.style.position = 'absolute';
  pdfContainer.style.left = '-9999px'; 
  pdfContainer.style.width = '1100px'; 
  pdfContainer.style.padding = '40px'; 
  pdfContainer.style.fontFamily = 'Arial, sans-serif';
  pdfContainer.style.color = '#333333'; 
  pdfContainer.style.backgroundColor = '#FFFFFF';
  pdfContainer.style.boxSizing = 'border-box';

  const headerHtml = `
    <div style="border-top: 5px solid #1A1A2E; margin-bottom: 25px;"></div> 
    <div style="display: flex; align-items: center; padding-bottom: 20px; margin-bottom: 25px;">
      <img src="${LOGO_URL_DARK_MODE}" alt="HBA Creative Group Logo" style="height: 55px; margin-right: 20px;" crossorigin="anonymous" />
      <div>
        <h1 style="font-size: 28px; font-weight: bold; margin: 0 0 5px 0; color: #1A1A2E;">Plano de Distribución y Listas</h1>
        <p style="font-size: 16px; margin: 0; color: #555555;">Mesa Planner - HBA Creative Group</p>
      </div>
    </div>
  `;

  let eventInfoHtml = '<div style="margin-bottom: 30px; font-size: 12px; line-height: 1.6;">';
  eventInfoHtml += `<h2 style="font-size: 18px; font-weight: bold; color: #1A1A2E; margin-bottom: 12px; border-bottom: 1px solid #dddddd; padding-bottom: 6px;">Configuración del Evento</h2>`;
  
  const venueWidthNum = parseFloat(formData.venueWidth);
  const venueLengthNum = parseFloat(formData.venueLength);
  
  eventInfoHtml += `<p style="margin: 4px 0;"><strong>Dimensiones del salón:</strong> ${venueWidthNum.toFixed(2)}m x ${venueLengthNum.toFixed(2)}m</p>`;
  eventInfoHtml += `<p style="margin: 4px 0;"><strong>Total de invitados (objetivo):</strong> ${formData.guests}</p>`;
  
  const standardTables = allTables.filter(t => !t.isMainTable);
  const mainTable = allTables.find(t => t.isMainTable);
  
  const tableShapesCount: { [key: string]: number } = {};
  standardTables.forEach(t => {
    const shapeLabel = getShapeLabel(t.shape);
    tableShapesCount[shapeLabel] = (tableShapesCount[shapeLabel] || 0) + 1;
  });
  const tableShapesString = Object.entries(tableShapesCount).map(([shape, count]) => `${count} ${shape}${count > 1 ? 's' : ''}`).join(', ') || 'N/A';

  eventInfoHtml += `<p style="margin: 4px 0;"><strong>Mesas/Livings:</strong> ${standardTables.length} (${tableShapesString}) - ${formData.seatsPerStandardTable} asientos/unidad (nominal).</p>`;
  
  if (mainTable) {
    eventInfoHtml += `<p style="margin: 4px 0;"><strong>Mesa Principal:</strong> 1 mesa ${getShapeLabel(mainTable.shape)} (${mainTable.seats} asientos capacidad, ${mainTable.guestList.length} nombrados)</p>`;
  }
  
  if (danceFloorConfig.enabled) {
    const customWidth = parseFloat(formData.danceFloorCustomWidth || '');
    const customHeight = parseFloat(formData.danceFloorCustomHeight || '');
    let dfWidth = danceFloorConfig.width;
    let dfHeight = danceFloorConfig.height;
    let type = "sugeridas";

    if(!isNaN(customWidth) && customWidth > 0 && !isNaN(customHeight) && customHeight > 0){
        dfWidth = customWidth;
        dfHeight = customHeight;
        type = "personalizadas";
    }
    eventInfoHtml += `<p style="margin: 4px 0;"><strong>Pista de Baile:</strong> ${dfWidth.toFixed(2)}m x ${dfHeight.toFixed(2)}m (dimensiones ${type})</p>`;
  }

  if (customElements.length > 0) {
    eventInfoHtml += `<div style="margin-top: 10px;">`;
    eventInfoHtml += `<h3 style="font-size: 14px; font-weight: bold; color: #1A1A2E; margin-bottom: 6px;">Otros Elementos:</h3>`;
    eventInfoHtml += `<ul style="list-style-type: none; padding-left: 0; margin:0;">`;
    customElements.forEach(el => {
      eventInfoHtml += `<li style="margin: 2px 0;">- ${el.name || 'Elemento sin nombre'}: ${el.width.toFixed(2)}m x ${el.height.toFixed(2)}m</li>`;
    });
    eventInfoHtml += `</ul></div>`;
  }
  
  eventInfoHtml += `<p style="font-size: 10px; color: #777777; margin-top: 15px;">Generado el: ${new Date().toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}hs</p>`;
  eventInfoHtml += '<div style="border-bottom: 1px solid #dddddd; margin-top:15px;"></div></div>';

  const floorPlanImagePlaceholderId = `floorPlanImagePlaceholder-${Date.now()}`;
  const floorPlanHtml = `<div id="${floorPlanImagePlaceholderId}" style="text-align: center; margin-bottom: 30px; border: 1px solid #cccccc; padding: 10px; background-color: #f9f9f9;"></div>`;

  // --- Listas de Invitados ---
  let guestListHtml = '<div style="margin-top: 30px; font-size: 12px; line-height: 1.5;">';
  guestListHtml += `<h2 style="font-size: 18px; font-weight: bold; color: #1A1A2E; margin-bottom: 15px; border-bottom: 1px solid #dddddd; padding-bottom: 6px;">Listas de Invitados por Mesa</h2>`;
  
  let guestsListed = false;
  allTables.sort((a,b) => { 
    if (a.isMainTable && !b.isMainTable) return -1;
    if (!a.isMainTable && b.isMainTable) return 1;
    return a.displayId.localeCompare(b.displayId, undefined, {numeric: true});
  }).forEach(table => {
    if (table.guestList && table.guestList.length > 0) {
      guestsListed = true;
      guestListHtml += `<div style="margin-bottom: 15px; padding-left: 10px; border-left: 3px solid #${table.isMainTable ? 'FFC107' : '7F00FF'};">`; 
      const tableType = table.shape === 'living' ? 'Living' : 'Mesa';
      guestListHtml += `<h3 style="font-size: 14px; font-weight: bold; color: #333333; margin-bottom: 5px;">${tableType} ${table.displayId} (${getShapeLabel(table.shape)}${table.isMainTable ? ', Principal' : ''}) - ${table.guestList.length} de ${table.seats} asientos ocupados</h3>`;
      guestListHtml += '<ul style="list-style-type: disc; margin-left: 20px; padding-left: 0;">';
      table.guestList.forEach(guest => {
        guestListHtml += `<li style="margin-bottom: 3px;">${guest}</li>`;
      });
      guestListHtml += '</ul></div>';
    }
  });

  if (!guestsListed) {
    guestListHtml += '<p style="color: #555555; font-style: italic;">No se han asignado invitados a ninguna mesa.</p>';
  }
  guestListHtml += '<div style="border-bottom: 1px solid #dddddd; margin-top:15px;"></div></div>';


  const currentYear = new Date().getFullYear();
  const footerLinkStyle = "color: #333333; text-decoration: none;"; 
  const footerTextStyle = "color: #555555; margin-bottom: 3px; display: block;"; 
  const footerTitleStyle = "color: #333333; font-weight: bold; margin-bottom: 6px; display: block;";

  const footerHtml = `
    <div style="border-top: 1px solid #dddddd; padding-top: 20px; margin-top: 40px; font-size: 10px;">
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="width: 33%; vertical-align: top; padding-right: 15px; line-height: 1.5;">
            <span style="${footerTitleStyle}">HBA Creative Group</span>
            <span style="${footerTextStyle}">By Aldo Irazoqui</span>
            <a href="https://housebuenosaires.com/" target="_blank" style="${footerLinkStyle}">housebuenosaires.com</a><br/>
            <span style="${footerTextStyle}">Palermo, Buenos Aires</span>
          </td>
          <td style="width: 33%; vertical-align: top; padding-left: 15px; padding-right: 15px; line-height: 1.5;">
            <span style="${footerTitleStyle}">Contacto:</span>
            <a href="mailto:contacto@housebuenosaires.com" style="${footerLinkStyle} display: block; margin-bottom: 3px;">contacto@housebuenosaires.com</a>
            <a href="https://wa.link/2eec1a" target="_blank" style="${footerLinkStyle} display: block; margin-bottom: 3px;">WhatsApp: +54 11 35647277</a>
            <a href="tel:+541135647277" style="${footerLinkStyle} display: block; margin-bottom: 3px;">Tel: +54 11 35647277</a>
          </td>
          <td style="width: 33%; vertical-align: top; padding-left: 15px; line-height: 1.5;">
            <span style="${footerTitleStyle}">Síguenos:</span>
            <a href="https://www.instagram.com/hbadjs/" target="_blank" style="${footerLinkStyle} display: block; margin-bottom: 3px;">Instagram</a>
            <a href="https://www.linkedin.com/in/aldoirazoqui/" target="_blank" style="${footerLinkStyle} display: block; margin-bottom: 3px;">LinkedIn</a>
          </td>
        </tr>
      </table>
      <div style="text-align: center; margin-top: 25px; padding-top: 15px; border-top: 1px solid #eeeeee; font-size: 9px; color: #888888;">
        &copy; ${currentYear} HBA Creative Group. Todos los derechos reservados.
      </div>
    </div>
  `;

  pdfContainer.innerHTML = headerHtml + eventInfoHtml + floorPlanHtml + guestListHtml + footerHtml;
  document.body.appendChild(pdfContainer);

  try {
    // Fix: Removed explicit type argument from html2canvas call.
    const floorPlanCanvasElement = await html2canvas(floorPlanElement, {
      scale: 2, 
      useCORS: true,
      logging: false,
      backgroundColor: '#FFFFFF', 
      onclone: (clonedDoc) => {
        if (!clonedDoc.head) {
            const head = clonedDoc.createElement('head');
            clonedDoc.documentElement.insertBefore(head, clonedDoc.body);
        }
        const styleTag = clonedDoc.createElement('style');
        styleTag.type = 'text/css';
        styleTag.innerHTML = `
          .pdf-ruler-override { background-color: #FFFFFF !important; }
          .pdf-ruler-label-override { color: #000000 !important; }
          .pdf-ruler-tick-override { background-color: #505050 !important; }
        `;
        clonedDoc.head.appendChild(styleTag);

        const mainCanvasArea = clonedDoc.querySelector(`[data-canvas-id="floor-plan-main-bg"]`) as HTMLElement | null; // Updated selector
        if (mainCanvasArea) {
          mainCanvasArea.style.backgroundColor = '#FFFFFF'; 
          mainCanvasArea.classList.remove('bg-hba-deep-purple', 'bg-gray-200');
          mainCanvasArea.classList.add('bg-white'); 
          const gridLines = mainCanvasArea.querySelectorAll('div[style*="background-color"]');
          gridLines.forEach(line => {
            if ((line as HTMLElement).style.zIndex === '0') { 
                (line as HTMLElement).style.backgroundColor = 'rgba(204, 204, 204, 0.5)'; 
            }
          });
        }
        
        const rulers = clonedDoc.querySelectorAll('[data-type="ruler"]');
        rulers.forEach(ruler => {
          ruler.classList.remove('bg-hba-mid-purple', 'bg-gray-300');
          ruler.classList.add('pdf-ruler-override');

          const labels = ruler.querySelectorAll('[data-ruler-label="true"]');
          labels.forEach(label => {
            label.classList.remove('text-hba-text-secondary', 'text-gray-500', 'text-gray-700');
            label.classList.add('pdf-ruler-label-override');
          });

          const ticks = ruler.querySelectorAll('[data-ruler-tick]');
          ticks.forEach(tick => {
            tick.classList.remove('bg-hba-text-secondary', 'bg-gray-500');
            tick.classList.add('pdf-ruler-tick-override');
          });
        });
      }
    });
    const floorPlanImgData = floorPlanCanvasElement.toDataURL('image/png');
    
    const placeholderElement = pdfContainer.querySelector(`#${floorPlanImagePlaceholderId}`);
    if (placeholderElement) {
      const imgElement = document.createElement('img');
      imgElement.src = floorPlanImgData;
      imgElement.style.maxWidth = '100%';
      imgElement.style.height = 'auto';
      imgElement.style.display = 'block'; 
      imgElement.style.margin = '0 auto'; 
      placeholderElement.appendChild(imgElement);
    }

    // Fix: Removed explicit type argument from html2canvas call.
    const finalCanvas = await html2canvas(pdfContainer, {
      scale: 1.5, 
      useCORS: true,
      logging: false,
      windowWidth: pdfContainer.scrollWidth,
      windowHeight: pdfContainer.scrollHeight,
    });
    
    const finalImgData = finalCanvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: finalCanvas.width > finalCanvas.height ? 'l' : 'p',
      unit: 'px',
      format: [finalCanvas.width, finalCanvas.height]
    });
    pdf.addImage(finalImgData, 'PNG', 0, 0, finalCanvas.width, finalCanvas.height);
    pdf.save(fileName);

  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Ocurrió un error al generar el PDF. Por favor, inténtelo de nuevo.");
  } finally {
    if (pdfContainer.parentNode === document.body) {
        document.body.removeChild(pdfContainer);
    }
  }
};
